import { useHistory, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useCallback, useEffect } from 'react';
import { Spin } from 'antd';

import {
  GENERATE_INVOICE_REPORT_MUTATION,
  GET_STATEMENT_LIST_QUERY,
} from '../../app/graphql/invoice';
import { useFlashMessageContext } from '../../components/dialogs/FlashMessageProvider';
import InvoiceStatementResultsDisplay from '../invoice/InvoiceStatementResultsDisplay';
import InvoiceContainer from '../invoice/InvoiceContainer';
import { useEmailProcessingDialog } from '../useEmailProcessingDialog';
import { extractGraphqlEntity } from '../../common/utils/graphqlUtils';
import { useInvoiceStyles } from '../invoice/invoice.styles';

export default function StatementResults() {
  const { openDialog, dialog } = useEmailProcessingDialog({
    documentType: 'Statement List',
    paragraph2Id: 'emailProcessingDialog.paragraph2.withLink',
  });
  const { errorMessage } = useFlashMessageContext();
  const history = useHistory();
  const location = useLocation();
  const { state: searchParams } = location || {};
  const accountNumber = searchParams?.id?.number;
  const { styles } = useInvoiceStyles();

  const { data, loading, error } = useQuery(GET_STATEMENT_LIST_QUERY, {
    variables: {
      id: accountNumber,
    },
    skip: !searchParams,
    errorPolicy: 'all',
  });

  const statementData = data?.getStatementList?.data;
  const hasData = statementData?.length > 0;
  const statementCount = statementData?.[0].invoiceNumber?.length ?? 0;

  const actionButtons = [
    {
      labelId: 'invoice.download',
      icon: 'download',
      mutationProps: {
        mutation: GENERATE_INVOICE_REPORT_MUTATION,
        mutationOptions: {
          onCompleted: statementReportData => {
            const { status } = extractGraphqlEntity(statementReportData);
            if (status !== 'SUBMITTED') {
              errorMessage({ contentId: 'invoice.emailList.error' });
            } else {
              openDialog();
            }
          },
          onError: () => {
            errorMessage({ contentId: 'invoice.emailList.error' });
          },
        },
        getMutationArgs: () => ({
          variables: {
            accountNumber,
            reportName: 'INV.LIST.EMAIL',
          },
        }),
      },
    },
  ];

  const handleBack = useCallback(() => {
    history.goBack();
  }, [history]);

  useEffect(() => {
    if (error) {
      errorMessage({ contentId: 'error.appError' });
      handleBack();
    } else if (!loading && searchParams && !hasData) {
      errorMessage({ contentId: 'statement.noStatementFound' });
      handleBack();
    }
  }, [error, loading, searchParams, hasData, errorMessage, handleBack]);

  if (loading || !searchParams) {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <InvoiceContainer
        handleBack={handleBack}
        titleCount={statementCount}
        titleId="account.statement"
        actionButtons={actionButtons}
      >
        <InvoiceStatementResultsDisplay
          accountName={searchParams?.id?.name}
          accountNumber={accountNumber}
          data={statementData}
        />
      </InvoiceContainer>
      {dialog}
    </>
  );
}
